'use client';

import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Star, Quote, MessageSquarePlus } from '@/lib/icons';
import { EASE_CUSTOM } from '@/lib/animations';

/**
 * URL de Google Business Profile para reseñas.
 * TODO: reemplazar con la URL real del Google Business Profile cuando esté creado/reclamado.
 * Formato: https://g.page/r/CdXXXXXXXXXXXXXX/review
 */
const GOOGLE_REVIEW_URL = 'https://g.page/r/INNOVATHERA-PLACEHOLDER/review';

/**
 * Reseñas reales publicadas. Vacío hasta que tengamos los testimonios escritos
 * de Astra Solutions, C22 Calisthenics y Moa Society (acción comercial pendiente).
 *
 * Estructura:
 *   { author, role, company, sector, text, rating (1-5), date }
 */
type Review = {
    author: string;
    role: string;
    company: string;
    sector: string;
    text: string;
    rating: number;
    date: string;
};

const reviews: Review[] = [
    // Pendiente: añadir reseñas reales tras pedirlas a Astra, C22 y Moa Society
];

export default function Resenas() {
    const hasReviews = reviews.length > 0;

    return (
        <section id="resenas" className="py-20 md:py-28 relative z-10">
            <div className="max-w-5xl mx-auto px-6 lg:px-8">

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.9, ease: EASE_CUSTOM }}
                    className="text-center mb-12"
                >
                    <div className="text-xs font-mono tracking-[0.3em] text-slate-400 uppercase mb-4">
                        Lo que dicen quienes ya trabajan con nosotros
                    </div>
                    <h2 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
                        Sin reseñas inventadas.{' '}
                        <span className="text-slate-400 font-light">Solo casos reales.</span>
                    </h2>
                </motion.div>

                {hasReviews ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {reviews.map((review, idx) => (
                            <motion.div
                                key={`${review.author}-${idx}`}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ duration: 0.8, delay: idx * 0.1, ease: EASE_CUSTOM }}
                                className="glass-card rounded-3xl p-7 flex flex-col gap-4"
                            >
                                <div className="flex items-start justify-between">
                                    <Quote className="w-7 h-7 text-blue-200" />
                                    <div className="flex items-center gap-0.5">
                                        {Array.from({ length: 5 }).map((_, i) => (
                                            <Star
                                                key={i}
                                                className={`w-3.5 h-3.5 ${i < review.rating ? 'text-blue-500 fill-blue-500' : 'text-slate-200'}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <p className="text-slate-700 leading-relaxed text-[15px]">
                                    {review.text}
                                </p>
                                <div className="mt-auto pt-4 border-t border-slate-200/60">
                                    <p className="font-bold text-slate-900 text-sm">{review.author}</p>
                                    <p className="text-slate-500 text-xs">
                                        {review.role} · {review.company}
                                    </p>
                                    <p className="text-slate-400 text-[11px] font-mono mt-1.5 uppercase tracking-wider">
                                        {review.sector} · {review.date}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                ) : (
                    /* Estado vacío honesto: estamos documentando casos */
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{ duration: 0.9, ease: EASE_CUSTOM }}
                        className="glass-card rounded-3xl p-10 md:p-14 text-center flex flex-col items-center gap-4"
                    >
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-50 border border-blue-100">
                            <Quote className="w-7 h-7 text-blue-600" />
                        </div>
                        <h3 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900">
                            Estamos documentando los primeros casos de éxito
                        </h3>
                        <p className="text-slate-500 max-w-lg leading-relaxed">
                            Preferimos publicar reseñas reales con métricas verificables a inventar testimonios.
                            Pronto compartiremos aquí los resultados de los proyectos que están en marcha.
                        </p>
                    </motion.div>
                )}

                {/* CTA: dejar reseña en Google */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2, ease: EASE_CUSTOM }}
                    className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                    <a
                        href={GOOGLE_REVIEW_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold shadow-[0_4px_16px_rgba(37,99,235,0.3)] hover:bg-blue-700 transition-all active:scale-95"
                    >
                        <MessageSquarePlus className="w-4 h-4" />
                        ¿Trabajaste con nosotros? Déjanos una reseña
                        <ArrowRight className="w-4 h-4" />
                    </a>
                    <span className="text-xs font-mono tracking-widest text-slate-400 uppercase">
                        Google Business Profile
                    </span>
                </motion.div>
            </div>
        </section>
    );
}
